import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, Chip, IconButton, Collapse, Button, Menu, MenuItem,
  Avatar, AvatarGroup, TextField, Select, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import { ChevronDown, ChevronRight, MoreHorizontal, Play, CheckCircle2, Plus, Kanban as KanbanIcon } from "lucide-react";
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { planService } from "../../api/services/planService";
import { identityService } from "../../api/services/identityService";
import { projectService } from "../../api/services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, ConfirmDialog, Loader } from "../../components/Common";
import TicketCreateDialog from "./TicketCreateDialog";

const CADENCE_DAYS = { weekly: 7, monthly: 30 };
const STATUS_COLORS = {
  open: { bg: "#E8E8E8", fg: "#111" },
  pending: { bg: "#FFF3CD", fg: "#8A6D00" },
  resolved: { bg: "#DDEEDD", fg: "#1B5E20" },
  closed: { bg: "#111111", fg: "#FFFFFF" },
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysIso(dateIso, days) {
  const d = new Date(dateIso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
const emptyForm = { name: "", goal: "", cadence: "weekly", start_date: todayIso(), end_date: addDaysIso(todayIso(), 7) };

/**
 * Backlog + Phases, on one page -- like Jira's Backlog view, but "Phase"
 * instead of "Sprint". Not-started/active phases render as collapsible
 * sections up top (active first), the unphased Backlog renders last.
 * Tickets can be dragged between sections; dropping into a phase/backlog
 * calls the same add/remove-to-plan endpoints the old separate pages used.
 */
export default function TicketBacklog() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("admin.plans.manage");

  const [expanded, setExpanded] = useState({});
  const [phaseDialog, setPhaseDialog] = useState(null); // null | "create" | <plan being edited>
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [createTicketFor, setCreateTicketFor] = useState(null); // null | "backlog" | planId
  const [activeTicket, setActiveTicket] = useState(null);

  const { data: project } = useQuery({ queryKey: ["project", projectId], queryFn: () => projectService.get(projectId) });
  const { data: statuses } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses, staleTime: Infinity });
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });
  const { data: plans } = useQuery({ queryKey: ["plans", { project_id: projectId }], queryFn: () => planService.list({ project_id: projectId }) });
  const { data: tickets, isLoading } = useQuery({
    queryKey: ["tickets-board", { project_id: projectId }],
    queryFn: () => ticketService.listBoard({ project_id: projectId }),
    enabled: Boolean(projectId),
  });

  const priorityById = useMemo(() => Object.fromEntries((priorities || []).map((p) => [p.id, p])), [priorities]);
  const employeeById = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);

  const ticketsByPlan = useMemo(() => {
    const map = { backlog: [] };
    for (const t of tickets || []) {
      const key = t.plan_id ? String(t.plan_id) : "backlog";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return map;
  }, [tickets]);

  const openPlans = useMemo(
    () =>
      (plans || [])
        .filter((p) => p.status !== "completed")
        .sort((a, b) => {
          if (a.status !== b.status) return a.status === "active" ? -1 : 1;
          return a.start_date.localeCompare(b.start_date);
        }),
    [plans]
  );

  const completedPlans = useMemo(
    () =>
      (plans || [])
        .filter((p) => p.status === "completed")
        .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || "")),
    [plans]
  );

  // Open phases/backlog default expanded; completed phases default collapsed
  // since they're history, not active planning -- keeps the page from
  // getting cluttered as more phases finish.
  const isExpanded = (key, defaultExpanded = true) => {
    const v = expanded[key];
    return v === undefined ? defaultExpanded : v;
  };
  const toggleExpanded = (key, defaultExpanded = true) =>
    setExpanded((e) => ({ ...e, [key]: !isExpanded(key, defaultExpanded) }));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tickets-board"] });
    queryClient.invalidateQueries({ queryKey: ["plans"] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ ticketId, statusId }) => ticketService.update(ticketId, { status_id: statusId }),
    onSuccess: invalidate,
    onError: (err) => { enqueueSnackbar(err.response?.data?.detail || "Couldn't update status", { variant: "error" }); invalidate(); },
  });

  const moveMutation = useMutation({
    mutationFn: ({ ticketId, toPlanId }) =>
      toPlanId ? planService.addTickets(toPlanId, [ticketId]) : planService.removeToBacklog([ticketId]),
    onSuccess: invalidate,
    onError: (err) => { enqueueSnackbar(err.response?.data?.detail || "Couldn't move that ticket", { variant: "error" }); invalidate(); },
  });

  const createPhaseMutation = useMutation({
    mutationFn: (form) => planService.create({
      name: form.name, goal: form.goal || null, project_id: Number(projectId), cadence: form.cadence,
      start_date: form.start_date, end_date: form.end_date,
    }),
    onSuccess: () => { enqueueSnackbar("Phase created", { variant: "success" }); setPhaseDialog(null); invalidate(); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't create phase", { variant: "error" }),
  });

  const updatePhaseMutation = useMutation({
    mutationFn: ({ id, form, scheduleLocked }) =>
      planService.update(id, scheduleLocked
        ? { name: form.name, goal: form.goal || null }
        : { name: form.name, goal: form.goal || null, start_date: form.start_date, end_date: form.end_date, cadence: form.cadence }),
    onSuccess: () => { enqueueSnackbar("Phase updated", { variant: "success" }); setPhaseDialog(null); invalidate(); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't update phase", { variant: "error" }),
  });

  const startMutation = useMutation({
    mutationFn: (id) => planService.start(id),
    onSuccess: () => { enqueueSnackbar("Phase started", { variant: "success" }); invalidate(); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't start phase", { variant: "error" }),
  });
  const completeMutation = useMutation({
    mutationFn: (id) => planService.complete(id),
    onSuccess: () => { enqueueSnackbar("Phase completed", { variant: "success" }); invalidate(); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't complete phase", { variant: "error" }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => planService.delete(id),
    onSuccess: () => { enqueueSnackbar("Phase deleted", { variant: "success" }); setDeleteTarget(null); invalidate(); },
    onError: (err) => { enqueueSnackbar(err.response?.data?.detail || "Couldn't delete phase", { variant: "error" }); setDeleteTarget(null); },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = (event) => setActiveTicket(event.active.data.current?.ticket || null);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTicket(null);
    if (!over) return;
    const ticket = active.data.current?.ticket;
    if (!ticket) return;
    const toPlanId = over.data.current?.planId ?? null; // null = backlog
    const fromPlanId = ticket.plan_id || null;
    if (fromPlanId === toPlanId) return;
    moveMutation.mutate({ ticketId: ticket.id, toPlanId });
  };

  const handleTicketCreated = (ticket) => {
    if (createTicketFor && createTicketFor !== "backlog") {
      planService.addTickets(createTicketFor, [ticket.id])
        .then(invalidate)
        .catch((err) => enqueueSnackbar(err.response?.data?.detail || "Ticket created, but couldn't add it to the phase", { variant: "warning" }));
    } else {
      invalidate();
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Box>
      <PageHeader
        title={project ? `${project.name} · Backlog` : "Backlog"}
        subtitle="Plan work into phases with a timeline, then start one to work it on a board"
        action={canManage && (
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setPhaseDialog("create")}>
            Create Phase
          </Button>
        )}
      />

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveTicket(null)}>
        <Stack spacing={2}>
          {openPlans.length === 0 && (
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 700 }}>No phase yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Use "Create Phase" above to set a name, goal, and timeline, then drag tickets into it from the Backlog below.
              </Typography>
            </Paper>
          )}
          {openPlans.map((plan) => (
            <PhaseSection
              key={plan.id}
              plan={plan}
              tickets={ticketsByPlan[String(plan.id)] || []}
              expanded={isExpanded(`plan-${plan.id}`)}
              onToggle={() => toggleExpanded(`plan-${plan.id}`)}
              priorityById={priorityById}
              employeeById={employeeById}
              statuses={statuses}
              canManage={canManage}
              onStart={() => startMutation.mutate(plan.id)}
              onComplete={() => completeMutation.mutate(plan.id)}
              onEdit={() => setPhaseDialog(plan)}
              onDelete={() => setDeleteTarget(plan)}
              onOpenBoard={() => navigate(`/tickets/p/${projectId}/plans/${plan.id}/board`)}
              onStatusChange={(ticketId, statusId) => statusMutation.mutate({ ticketId, statusId })}
              onTicketClick={(id) => navigate(`/tickets/${id}`)}
              onAddTicket={() => setCreateTicketFor(plan.id)}
            />
          ))}

          <BacklogSection
            tickets={ticketsByPlan.backlog || []}
            expanded={isExpanded("backlog")}
            onToggle={() => toggleExpanded("backlog")}
            priorityById={priorityById}
            employeeById={employeeById}
            statuses={statuses}
            canManage={canManage}
            onCreatePhase={() => setPhaseDialog("create")}
            onStatusChange={(ticketId, statusId) => statusMutation.mutate({ ticketId, statusId })}
            onTicketClick={(id) => navigate(`/tickets/${id}`)}
            onAddTicket={() => setCreateTicketFor("backlog")}
          />

          {completedPlans.length > 0 && (
            <Box sx={{ pt: 1 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, color: "text.secondary", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
              >
                Completed Phases ({completedPlans.length})
              </Typography>
              <Stack spacing={2}>
                {completedPlans.map((plan) => (
                  <PhaseSection
                    key={plan.id}
                    plan={plan}
                    tickets={ticketsByPlan[String(plan.id)] || []}
                    expanded={isExpanded(`plan-${plan.id}`, false)}
                    onToggle={() => toggleExpanded(`plan-${plan.id}`, false)}
                    priorityById={priorityById}
                    employeeById={employeeById}
                    statuses={statuses}
                    canManage={canManage}
                    onStart={() => {}}
                    onComplete={() => {}}
                    onEdit={() => setPhaseDialog(plan)}
                    onDelete={() => setDeleteTarget(plan)}
                    onOpenBoard={() => navigate(`/tickets/p/${projectId}/plans/${plan.id}/board`)}
                    onStatusChange={(ticketId, statusId) => statusMutation.mutate({ ticketId, statusId })}
                    onTicketClick={(id) => navigate(`/tickets/${id}`)}
                    onAddTicket={() => {}}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>

        <DragOverlay>
          {activeTicket && (
            <Paper variant="outlined" sx={{ p: 1, px: 1.5, boxShadow: 4, width: 500 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{activeTicket.ticket_number} — {activeTicket.subject}</Typography>
            </Paper>
          )}
        </DragOverlay>
      </DndContext>

      <PhaseFormDialog
        open={Boolean(phaseDialog)}
        plan={phaseDialog && phaseDialog !== "create" ? phaseDialog : null}
        onClose={() => setPhaseDialog(null)}
        onSubmit={(form) => {
          if (phaseDialog && phaseDialog !== "create") {
            updatePhaseMutation.mutate({ id: phaseDialog.id, form, scheduleLocked: phaseDialog.status !== "not_started" });
          } else {
            createPhaseMutation.mutate(form);
          }
        }}
        loading={createPhaseMutation.isPending || updatePhaseMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete phase?"
        message={`This deletes "${deleteTarget?.name}" and moves any tickets in it back to the Backlog.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />

      <TicketCreateDialog
        open={Boolean(createTicketFor)}
        projectId={projectId}
        onClose={() => setCreateTicketFor(null)}
        onCreated={handleTicketCreated}
      />
    </Box>
  );
}

function SectionHeaderStats({ tickets, statuses }) {
  const buckets = useMemo(() => {
    const statusById = Object.fromEntries((statuses || []).map((s) => [s.id, s]));
    let todo = 0, inProgress = 0, done = 0;
    for (const t of tickets) {
      const s = statusById[t.status_id];
      if (!s) continue;
      if (s.state_group === "resolved" || s.state_group === "closed") done++;
      else if (s.is_default) todo++;
      else inProgress++;
    }
    return { todo, inProgress, done };
  }, [tickets, statuses]);

  return (
    <Stack direction="row" spacing={0.5}>
      <Chip size="small" label={buckets.todo} sx={{ bgcolor: "#EEE", color: "#555", fontWeight: 700, minWidth: 30 }} />
      <Chip size="small" label={buckets.inProgress} sx={{ bgcolor: "#CFE3FD", color: "#0B5FCC", fontWeight: 700, minWidth: 30 }} />
      <Chip size="small" label={buckets.done} sx={{ bgcolor: "#D7F5E3", color: "#0B7D42", fontWeight: 700, minWidth: 30 }} />
    </Stack>
  );
}

function InlineStatusSelect({ value, statuses, onChange }) {
  const status = (statuses || []).find((s) => s.id === value);
  const c = STATUS_COLORS[status?.state_group] || STATUS_COLORS.open;
  return (
    <Select
      size="small" value={value} onChange={(e) => onChange(Number(e.target.value))}
      onClick={(e) => e.stopPropagation()}
      sx={{
        minWidth: 130, height: 28, fontSize: 12, fontWeight: 700,
        bgcolor: c.bg, color: c.fg, flexShrink: 0,
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-select": { py: 0.25 },
      }}
    >
      {(statuses || []).map((s) => <MenuItem key={s.id} value={s.id} sx={{ fontSize: 13 }}>{s.name}</MenuItem>)}
    </Select>
  );
}

function TicketRow({ ticket, priorityById, employeeById, statuses, onStatusChange, onClick, dragDisabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `ticket-${ticket.id}`, data: { ticket }, disabled: dragDisabled,
  });
  const priority = priorityById[ticket.priority_id];
  const assignee = employeeById[ticket.assignee_employee_id];

  return (
    <Stack
      ref={setNodeRef} {...listeners} {...attributes}
      direction="row" alignItems="center" spacing={1.5}
      onClick={() => onClick(ticket.id)}
      sx={{
        px: 2, py: 1, borderTop: "1px solid", borderColor: "divider",
        cursor: dragDisabled ? "pointer" : "grab", opacity: isDragging ? 0.3 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>{ticket.ticket_number}</Typography>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{ticket.subject}</Typography>
      {priority && (
        <Tooltip title={priority.name}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priority.color, flexShrink: 0 }} />
        </Tooltip>
      )}
      <InlineStatusSelect value={ticket.status_id} statuses={statuses} onChange={(id) => onStatusChange(ticket.id, id)} />
      <Tooltip title={assignee ? assignee.name : "Unassigned"}>
        <Avatar sx={{ width: 24, height: 24, fontSize: 12, flexShrink: 0 }}>
          {assignee ? assignee.name?.[0]?.toUpperCase() : "?"}
        </Avatar>
      </Tooltip>
    </Stack>
  );
}

function PhaseSection({
  plan, tickets, expanded, onToggle, priorityById, employeeById, statuses, canManage,
  onStart, onComplete, onEdit, onDelete, onOpenBoard, onStatusChange, onTicketClick, onAddTicket,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `section-plan-${plan.id}`, data: { planId: plan.id } });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const dragDisabled = plan.status !== "not_started";

  const uniqueAssignees = useMemo(() => {
    const ids = [...new Set(tickets.map((t) => t.assignee_employee_id).filter(Boolean))];
    return ids.map((id) => employeeById[id]).filter(Boolean);
  }, [tickets, employeeById]);

  return (
    <Paper ref={setNodeRef} variant="outlined" sx={{ overflow: "hidden", ...(isOver ? { borderColor: "primary.main" } : {}) }}>
      <Box sx={{ px: 2, pt: 1.5, pb: plan.goal ? 0.5 : 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <IconButton size="small" onClick={onToggle}>{expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</IconButton>
          <Typography sx={{ fontWeight: 700 }}>{plan.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {plan.start_date} – {plan.end_date} ({tickets.length} work item{tickets.length === 1 ? "" : "s"})
          </Typography>
          <Box sx={{ flex: 1 }} />
          {uniqueAssignees.length > 0 && (
            <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 11 } }}>
              {uniqueAssignees.map((e) => <Avatar key={e.id}>{e.name?.[0]?.toUpperCase()}</Avatar>)}
            </AvatarGroup>
          )}
          <SectionHeaderStats tickets={tickets} statuses={statuses} />
          <Tooltip title="Open phase board">
            <IconButton size="small" onClick={onOpenBoard}><KanbanIcon size={16} /></IconButton>
          </Tooltip>
          {canManage && plan.status === "not_started" && (
            <Button size="small" variant="contained" startIcon={<Play size={14} />} onClick={onStart}>Start Phase</Button>
          )}
          {canManage && plan.status === "active" && (
            <Button size="small" variant="outlined" startIcon={<CheckCircle2 size={14} />} onClick={onComplete}>Complete Phase</Button>
          )}
          {canManage && (
            <>
              <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}><MoreHorizontal size={18} /></IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { setMenuAnchor(null); onEdit(); }}>Edit phase</MenuItem>
                <Tooltip
                  title={plan.status !== "not_started" ? "Only phases that haven't started yet can be deleted -- this one's ticket list is kept as history" : ""}
                  placement="left"
                >
                  <span>
                    <MenuItem disabled={plan.status !== "not_started"} onClick={() => { setMenuAnchor(null); onDelete(); }}>
                      Delete phase
                    </MenuItem>
                  </span>
                </Tooltip>
              </Menu>
            </>
          )}
        </Stack>
        {plan.goal && (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 5, pt: 0.25 }}>{plan.goal}</Typography>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box>
          {tickets.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              Drag tickets here from the Backlog, or create one directly.
            </Typography>
          )}
          {tickets.map((t) => (
            <TicketRow
              key={t.id} ticket={t} priorityById={priorityById} employeeById={employeeById}
              statuses={statuses} onStatusChange={onStatusChange} onClick={onTicketClick} dragDisabled={dragDisabled}
            />
          ))}
          {canManage && plan.status === "not_started" && (
            <Button size="small" startIcon={<Plus size={14} />} onClick={onAddTicket} sx={{ m: 1.5 }}>Create ticket</Button>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

function BacklogSection({
  tickets, expanded, onToggle, priorityById, employeeById, statuses, canManage,
  onCreatePhase, onStatusChange, onTicketClick, onAddTicket,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: "section-backlog", data: { planId: null } });

  return (
    <Paper ref={setNodeRef} variant="outlined" sx={{ overflow: "hidden", ...(isOver ? { borderColor: "primary.main" } : {}) }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
        <IconButton size="small" onClick={onToggle}>{expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</IconButton>
        <Typography sx={{ fontWeight: 700 }}>Backlog</Typography>
        <Typography variant="body2" color="text.secondary">({tickets.length} work item{tickets.length === 1 ? "" : "s"})</Typography>
        <Box sx={{ flex: 1 }} />
        <SectionHeaderStats tickets={tickets} statuses={statuses} />
        {canManage && <Button size="small" variant="outlined" onClick={onCreatePhase}>Create Phase</Button>}
      </Stack>
      <Collapse in={expanded}>
        <Box>
          {tickets.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>Backlog is empty.</Typography>
          )}
          {tickets.map((t) => (
            <TicketRow
              key={t.id} ticket={t} priorityById={priorityById} employeeById={employeeById}
              statuses={statuses} onStatusChange={onStatusChange} onClick={onTicketClick} dragDisabled={false}
            />
          ))}
          <Button size="small" startIcon={<Plus size={14} />} onClick={onAddTicket} sx={{ m: 1.5 }}>Create ticket</Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

function PhaseFormDialog({ open, plan, onClose, onSubmit, loading }) {
  const [form, setForm] = useState(emptyForm);
  const scheduleLocked = Boolean(plan) && plan.status !== "not_started";

  useEffect(() => {
    if (open) {
      setForm(plan
        ? { name: plan.name, goal: plan.goal || "", cadence: plan.cadence, start_date: plan.start_date, end_date: plan.end_date }
        : emptyForm);
    }
  }, [open, plan]);

  const handleCadenceChange = (cadence) => {
    setForm((f) => (cadence === "custom" ? { ...f, cadence } : { ...f, cadence, end_date: addDaysIso(f.start_date, CADENCE_DAYS[cadence]) }));
  };
  const handleStartDateChange = (start_date) => {
    setForm((f) => (f.cadence === "custom" ? { ...f, start_date } : { ...f, start_date, end_date: addDaysIso(start_date, CADENCE_DAYS[f.cadence]) }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{plan ? "Edit Phase" : "New Phase"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {scheduleLocked && (
            <Typography variant="body2" color="text.secondary">
              This phase has already started, so only its name and goal can still be changed.
            </Typography>
          )}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
          <TextField
            label="Goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}
            fullWidth multiline minRows={2} placeholder="What should this phase accomplish?"
          />
          <TextField
            select label="Cadence" value={form.cadence} onChange={(e) => handleCadenceChange(e.target.value)}
            fullWidth disabled={scheduleLocked}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start date" type="date" InputLabelProps={{ shrink: true }} fullWidth
              value={form.start_date} disabled={scheduleLocked}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
            <TextField
              label="End date" type="date" InputLabelProps={{ shrink: true }} fullWidth
              value={form.end_date} disabled={scheduleLocked || form.cadence !== "custom"}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.name || !form.start_date || !form.end_date || loading}
          onClick={() => onSubmit(form)}
        >
          {plan ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
